<script setup>
  import { handleError } from '@/utils/error'

  const { href } = useRequestURL()
  const config = useRuntimeConfig()
  const { fullPath, params, path, query } = useRoute()
  const client = useStrapiClient()
  const { user } = useUser()
  const dayjs = useDayjs()

  const applyModal = ref(null)
  const openApplyModal = () => applyModal.value.open()
  const hasApplied = ref(false)

  const jobFetcher = reactive({
    ...(await useAsyncData(`job-${params.slug}`, async () => {
      const options = {
        params: {
          fields: [
            'advantages',
            'applicationEmail',
            'answer1',
            'answer2',
            'answer3',
            'crushes',
            'canLetterQuestions',
            'clspContacts',
            'durationCount',
            'durationUnit',
            'enhanceWithAI',
            'experience',
            'expiresAt',
            'externalUrl',
            'imported',
            'mandatoryLetter',
            'partTime',
            'salaryMin',
            'salaryMax',
            'salaryPeriod',
            'salaryType',
            'slug',
            'status',
            'teleworkable',
            'title',
            'type',
            'jobSheetVisible'
          ],
          filters: { published: true },
          populate: {
            categories: { fields: ['name'] },
            employer: {
              fields: ['defaultCrushes', 'description', 'name', 'slug', 'visible', 'website'],
              populate: {
                logo: { fields: ['url'] },
                careerSite: { fields: ['active', 'slug'] },
                cover: { fields: ['url'] }
              }
            },
            header: {
              fields: ['title'],
              populate: {
                cover: { fields: ['url'] }
              }
            },
            jobSheet: { fields: ['url'] },
            locations: { fields: ['city', 'street', 'postalCode', 'name'] },
            sectors: { fields: ['name'] },
            seo: {
              fields: ['metaDescription', 'metaTitle', 'metaRobots', 'structuredData'],
              populate: {
                metaImage: { fields: ['url'] }
              }
            },
            speaker: {
              fields: ['firstName', 'function', 'lastName', 'linkedin', 'title'],
              populate: {
                photo: { fields: ['url'] }
              }
            }
          }
        }
      }
      if (query.preview?.length) options.query = { preview: query.preview }
      const res = await client(`/jobs/${params.slug}`, options)
      return res.data
    }))
  })

  if (jobFetcher.error) {
    throw createError({ statusCode: 404, statusMessage: 'Job not found', url: path, fatal: true })
  }

  const job = computed(() => {
    const j = jobFetcher.data
    if (!j) return null
    return {
      ...j,
      isExpired() {
        return j.expiresAt < dayjs().format('YYYY-MM-DD')
      },
      isExternal() {
        return !!j.externalUrl
      },
      isStatus(status) {
        return status === j.status
      }
    }
  })

  let j
  if (job.value) {
    j = job.value
    hasApplied.value = user.value?.hasApplied(j.id)

    const referrer = useReferrer()
    if (process.client) {
      const originUrl = sessionStorage.getItem(`job:${j.id}`)
      if (!originUrl && referrer.value) sessionStorage.setItem(`job:${j.id}`, referrer.value)
      referrer.value = null
    }

    if (process.client && !query.preview) {
      client(`/jobs/${j.id}/view`, {
        body: { url: href },
        method: 'POST'
      }).catch(error => handleError(error))
    }

    const seo = j.seo ?? {}
    useSeo({
      canonicalUrl: config.public.baseUrl + fullPath.split('?')[0],
      metaTitle: seo.metaTitle ?? `${j.title} - ${j.employer?.name}`,
      metaDescription: seo.metaDescription ?? j.answer2,
      metaRobots: seo.metaRobots,
      structuredData: !j.isExpired() ? seo.structuredData : null
    })

    useHead({
      meta: [{ hid: 'og:image', property: 'og:image', content: useStrapiMedia(seo.metaImage?.url ?? j.employer?.cover?.url) }]
    })
  }
</script>

<template>
  <Loading class="mx-auto max-w-screen-xl px-4 py-16" v-if="jobFetcher.pending" />
  <template v-else-if="job">
    <div class="font-special">
      <JobHeader
        :imported="job.imported"
        :cover="job.header?.cover ? job.header.cover : job.employer?.cover"
        :employer="job.employer"
        :title="job.title"
      />
      <div class="flex min-h-full">
        <div class="flex-1 bg-zinc-100" />
        <div class="w-full max-w-screen-xl pt-32 font-main lg:flex lg:pt-36">
          <JobSidebar :isCareerSite="false" :job="job" @apply="openApplyModal" />
          <JobBody :job="job" @apply="openApplyModal" />
        </div>
        <div class="flex-1" />
      </div>

      <ModalApply
        :job="job"
        :mandatoryLetter="job.mandatoryLetter"
        :canLetterQuestions="job.canLetterQuestions"
        :otherFileField="true"
        ref="applyModal"
        @applied="hasApplied = true"
      />
    </div>
  </template>
</template>
